/**
 * Tarefas do PCP: ler o manual ou executar. Quem edita também cria tarefas
 * novas, organiza a ordem (arrastando ou pelas setas), ativa/inativa, edita
 * os motivos prontos e vê a lixeira. Nenhuma tarefa é especial no código:
 * as 8 iniciais são dados como qualquer outra.
 *
 * Online lê do servidor (inclui rascunhos para quem edita); sem internet,
 * mostra as tarefas ativas guardadas no aparelho.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiArchive, FiEdit3, FiPlus, FiRotateCcw, FiSliders } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import AsyncButton from "../../components/AsyncButton";
import { EmptyState, EmptyStateErro } from "../../components/EmptyState";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useAuth } from "../../context/AuthContext";
import { ehFalhaDeRede, listarTarefas, organizarTarefas, restaurarTarefa } from "../../services/areaPcp";
import Casca, { BASE } from "./Casca";
import IniciarExecucao from "./componentes/IniciarExecucao";
import ListaOrdenavel, { SetasDeOrdem } from "./componentes/ListaOrdenavel";
import { dataHoraLocal } from "./dominio/formatos";
import MotivosPainel from "./MotivosPainel";
import { useAreaPcp } from "./offline/AreaPcpContext";
import { PERM_EDITAR, PERM_EXECUTAR } from "./permissoes";
import {
  Aviso,
  BotaoPrimario,
  BotaoSecundario,
  Corpo,
  Linha,
  Lista,
  Meta,
  Segmentado,
  Selo,
  Sub,
  TituloLinha,
} from "./styles";

const MODO = Object.freeze({ LISTA: "lista", ORGANIZAR: "organizar", LIXEIRA: "lixeira" });

/** Tarefas do pacote do aparelho no formato da lista (para quando não há rede). */
function tarefasDoPacote(pacote) {
  const abertas = (pacote?.execucoes || []).reduce((mapa, e) => {
    mapa[e.tarefa.id] = (mapa[e.tarefa.id] || 0) + 1;
    return mapa;
  }, {});
  return (pacote?.tarefas || []).map((t) => ({ ...t, n_passos: t.passos.length, n_abertas: abertas[t.id] || 0 }));
}

function useListaDeTarefas(apagadas) {
  const { request, hasPermission } = useAuth();
  const { pacote } = useAreaPcp();
  const [estado, setEstado] = useState({ carregando: true, tarefas: [], offline: false, erro: null });

  const carregar = useCallback(async () => {
    try {
      const resposta = await listarTarefas(request, { apagadas });
      setEstado({ carregando: false, tarefas: resposta.tarefas, offline: false, erro: null });
    } catch (erro) {
      if (ehFalhaDeRede(erro)) setEstado((atual) => ({ ...atual, carregando: false, offline: true, erro: null }));
      else setEstado({ carregando: false, tarefas: [], offline: false, erro });
    }
  }, [request, apagadas]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const tarefas = estado.offline && !apagadas ? tarefasDoPacote(pacote) : estado.tarefas;
  return {
    ...estado,
    tarefas,
    recarregar: carregar,
    podeEditar: hasPermission(PERM_EDITAR),
    podeExecutar: hasPermission(PERM_EXECUTAR),
  };
}

function LinhaTarefa({ tarefa, numero, podeExecutar, podeEditar, onLer, onEditar, onExecutar }) {
  return (
    <Linha>
      <Corpo>
        <TituloLinha>
          {numero}. {tarefa.titulo}
        </TituloLinha>
        {tarefa.quando ? <Sub>{tarefa.quando}</Sub> : null}
        <Meta>
          <span>{tarefa.n_passos} passos</span>
          {tarefa.n_abertas ? <span>{tarefa.n_abertas === 1 ? "1 aberta" : `${tarefa.n_abertas} abertas`}</span> : null}
          {!tarefa.ativo ? <Selo $tom="neutral">Rascunho · só quem edita vê</Selo> : null}
        </Meta>
      </Corpo>
      <BotaoSecundario type="button" onClick={onLer}>
        Ler manual
      </BotaoSecundario>
      {/* Editar à vista na lista: escondido só dentro do manual, ninguém achava. */}
      {podeEditar ? (
        <BotaoSecundario type="button" onClick={onEditar} aria-label={`Editar ${tarefa.titulo}`}>
          <FiEdit3 size={13} aria-hidden="true" /> Editar
        </BotaoSecundario>
      ) : null}
      {podeExecutar && tarefa.ativo ? (
        <BotaoPrimario type="button" onClick={onExecutar}>
          Executar
        </BotaoPrimario>
      ) : null}
    </Linha>
  );
}

function Organizar({ tarefas, onSalvo, onCancelar }) {
  const { request } = useAuth();
  const [itens, setItens] = useState(() => tarefas.map((t) => ({ id: t.id, titulo: t.titulo, ativo: t.ativo, n_passos: t.n_passos })));
  const mover = (de, para) => {
    if (para < 0 || para >= itens.length) return;
    const copia = [...itens];
    const [item] = copia.splice(de, 1);
    copia.splice(para, 0, item);
    setItens(copia);
  };
  const salvar = async () => {
    await organizarTarefas(request, itens.map(({ id, ativo }) => ({ id, ativo })));
    onSalvo();
  };
  return (
    <>
      <Aviso $tom="info">Arraste pela alça ou use ↑ ↓. Tarefa inativa (rascunho) some para quem só lê e executa.</Aviso>
      <Lista as="div" aria-label="Organizar tarefas">
        <ListaOrdenavel
          itens={itens}
          chave={(item) => item.id}
          onMover={mover}
          rotuloDoItem={(item) => item.titulo}
          renderItem={(item, indice, alca) => (
            <Linha as="div">
              {alca}
              <Corpo>
                <TituloLinha>
                  {indice + 1}. {item.titulo}
                </TituloLinha>
                <Meta>{item.n_passos} passos</Meta>
              </Corpo>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--ink-2)" }}>
                <input
                  type="checkbox"
                  checked={item.ativo}
                  onChange={(e) => setItens(itens.map((t) => (t.id === item.id ? { ...t, ativo: e.target.checked } : t)))}
                />
                Ativa
              </label>
              <SetasDeOrdem indice={indice} total={itens.length} rotulo={item.titulo} onMover={mover} />
            </Linha>
          )}
        />
      </Lista>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <BotaoSecundario type="button" onClick={onCancelar}>
          Cancelar
        </BotaoSecundario>
        <AsyncButton onClick={salvar} successMessage="Ordem salva">
          Salvar ordem
        </AsyncButton>
      </div>
    </>
  );
}

function Lixeira({ tarefas, onRestaurada }) {
  const { request } = useAuth();
  if (!tarefas.length) {
    return <EmptyState tone="ok" titulo="Lixeira vazia" descricao="Tarefas apagadas aparecem aqui e podem ser restauradas." />;
  }
  return (
    <Lista aria-label="Tarefas apagadas">
      {tarefas.map((tarefa) => (
        <Linha key={tarefa.id}>
          <Corpo>
            <TituloLinha>{tarefa.titulo}</TituloLinha>
            <Meta>
              Apagada por {tarefa.apagada_por || "—"} em {dataHoraLocal(tarefa.apagada_em)}
            </Meta>
          </Corpo>
          <AsyncButton
            variant="secondary"
            successMessage="Tarefa restaurada como rascunho"
            onClick={async () => {
              await restaurarTarefa(request, tarefa.id);
              onRestaurada();
            }}
          >
            <FiRotateCcw size={14} aria-hidden="true" /> Restaurar
          </AsyncButton>
        </Linha>
      ))}
    </Lista>
  );
}

export default function Tarefas() {
  const navigate = useNavigate();
  const { pacote } = useAreaPcp();
  const [modo, setModo] = useState(MODO.LISTA);
  const [motivosAbertos, setMotivosAbertos] = useState(false);
  const [aIniciar, setAIniciar] = useState(null);
  const lista = useListaDeTarefas(modo === MODO.LIXEIRA);
  const abertas = useMemo(() => (pacote?.execucoes || []).length, [pacote]);

  const acoesDoEditor = lista.podeEditar && !lista.offline ? (
    <>
      <BotaoSecundario type="button" onClick={() => setMotivosAbertos(true)} aria-label="Motivos prontos">
        <FiSliders size={14} aria-hidden="true" />
        <span className="so-desktop">Motivos</span>
      </BotaoSecundario>
      <BotaoPrimario type="button" onClick={() => navigate(`${BASE}/tarefas/nova`)}>
        <FiPlus size={14} aria-hidden="true" />
        <span>Nova tarefa</span>
      </BotaoPrimario>
    </>
  ) : null;

  let conteudo;
  if (lista.carregando) conteudo = <PageLoader scope="kpis" />;
  else if (lista.erro) conteudo = <EmptyStateErro erro={lista.erro} onRetry={lista.recarregar} />;
  else if (modo === MODO.ORGANIZAR) {
    conteudo = (
      <Organizar
        tarefas={lista.tarefas}
        onCancelar={() => setModo(MODO.LISTA)}
        onSalvo={() => {
          setModo(MODO.LISTA);
          lista.recarregar();
        }}
      />
    );
  } else if (modo === MODO.LIXEIRA) {
    conteudo = <Lixeira tarefas={lista.tarefas} onRestaurada={lista.recarregar} />;
  } else if (!lista.tarefas.length) {
    conteudo = (
      <EmptyState
        titulo="Nenhuma tarefa ainda"
        descricao={lista.podeEditar ? "Crie a primeira tarefa com seus passos." : "Quem edita os manuais ainda não publicou nenhuma tarefa."}
        acoes={lista.podeEditar ? [{ label: "Nova tarefa", onClick: () => navigate(`${BASE}/tarefas/nova`) }] : []}
      />
    );
  } else {
    conteudo = (
      <Lista aria-label="Tarefas">
        {lista.tarefas.map((tarefa, indice) => (
          <LinhaTarefa
            key={tarefa.id}
            tarefa={tarefa}
            numero={indice + 1}
            podeExecutar={lista.podeExecutar}
            podeEditar={lista.podeEditar && !lista.offline}
            onLer={() => navigate(`${BASE}/tarefas/${tarefa.id}`)}
            onEditar={() => navigate(`${BASE}/tarefas/${tarefa.id}/editar`)}
            onExecutar={() => setAIniciar(tarefa)}
          />
        ))}
      </Lista>
    );
  }

  return (
    <Casca contagemAbertas={abertas} acoes={acoesDoEditor}>
      {lista.offline ? <Aviso $tom="warning">Sem conexão: mostrando as tarefas guardadas neste aparelho.</Aviso> : null}
      {lista.podeEditar && !lista.offline ? (
        <Segmentado role="group" aria-label="Modo da lista" style={{ alignSelf: "flex-start" }}>
          <button type="button" aria-pressed={modo === MODO.LISTA} onClick={() => setModo(MODO.LISTA)}>
            Tarefas
          </button>
          <button type="button" aria-pressed={modo === MODO.ORGANIZAR} onClick={() => setModo(MODO.ORGANIZAR)}>
            Organizar
          </button>
          <button type="button" aria-pressed={modo === MODO.LIXEIRA} onClick={() => setModo(MODO.LIXEIRA)}>
            <FiArchive size={13} aria-hidden="true" /> Apagadas
          </button>
        </Segmentado>
      ) : null}
      {conteudo}
      <IniciarExecucao tarefa={aIniciar} aberta={Boolean(aIniciar)} onFechar={() => setAIniciar(null)} />
      {lista.podeEditar ? <MotivosPainel aberto={motivosAbertos} onFechar={() => setMotivosAbertos(false)} /> : null}
    </Casca>
  );
}
